/**
 * CyberSweep Audit Business Logic
 *
 * Handles automatic status updates when PAW findings change.
 * Implements:
 *   Flow 1: PAW Findings → Phase Status transitions
 *   Flow 2: Phase Status → Account Status transitions
 *   Flow 3: Account Status → Create Next CS Audit
 */

import { fetchAuditTemplateByName, GobodoAuditTemplate } from '../queries/cyberSweepQueries';
import { AUDIT_TEMPLATES } from '../../data/auditTemplates';

export interface PAWRecord {
  id: string;
  Audit_Phase__c: 'Phase 1' | 'Phase 2';
  Findings__c: string | null;
  Phase_2_Required__c?: boolean;
  Audit_Template__c?: string;
  // ... other fields
}

export interface CSAuditRecord {
  id: string;
  Audit_Template__c: string;
  Phase_Status__c: string;
  phaseStatus?: string;
  Phase_2_Required__c: boolean;
  Account__c?: string;
  accountId?: string;
  // Phase date/time fields
  Phase_1_Start_Date_Time__c?: string;
  phase1StartDateTime?: string;
  Phase_2_Start_Date_Time__c?: string;
  phase2StartDateTime?: string;
  Phase_1_Completed_Date_Time__c?: string;
  phase1CompletedDateTime?: string;
  Phase_2_Completed_Date_Time__c?: string;
  phase2CompletedDateTime?: string;
  // Last Modified fields
  LastModifiedDate__c?: string;
  lastModifiedDate?: string;
  LastModifiedBy__c?: string;
  lastModifiedBy?: string;
  // ... other fields
}

export interface AccountRecord {
  id: string;
  CS_Account_Status__c: string;
  Name?: string;
  name?: string;
  // ... other fields
}

/**
 * Flow 2: When audit completes, update Account Status
 *
 * Mapping:
 * - Initial Deletion → Account Status = "Awaiting Initial Report"
 * - Initial Audit → Account Status = "Awaiting Final Report"
 * - Final Audit → Account Status = "Quarterly Report 1"
 * - Quarter #1 Audit → Account Status = "Quarterly Report 2"
 * - Quarter #2 Audit → Account Status = "Quarterly Report 3"
 * - Quarter #3 Audit → Account Status = "Quarterly Report 4"
 * - Quarter #4 Audit → Account Status = "Quarterly Report 1" (cycles back)
 */
export const TEMPLATE_TO_ACCOUNT_STATUS: Record<string, string> = {
  'Initial Deletion': 'Awaiting Initial Report',
  'Initial Audit': 'Awaiting Final Report',
  'Final Audit': 'Quarterly Report 1',
  'Quarter #1 Audit': 'Quarterly Report 2',
  'Quarter #2 Audit': 'Quarterly Report 3',
  'Quarter #3 Audit': 'Quarterly Report 4',
  'Quarter #4 Audit': 'Quarterly Report 1', // Cycles back
};

/**
 * Flow 3: When Account Status changes, create the next CS Audit
 *
 * Mapping:
 * - "Awaiting Initial Report" → Create "Initial Audit"
 * - "Awaiting Final Report" → Create "Final Audit"
 * - "Quarterly Report 1" → Create "Quarter #1 Audit"
 * - "Quarterly Report 2" → Create "Quarter #2 Audit"
 * - "Quarterly Report 3" → Create "Quarter #3 Audit"
 * - "Quarterly Report 4" → Create "Quarter #4 Audit"
 */
export const ACCOUNT_STATUS_TO_TEMPLATE: Record<string, string> = {
  'Awaiting Initial Report': 'Initial Audit',
  'Awaiting Final Report': 'Final Audit',
  'Quarterly Report 1': 'Quarter #1 Audit',
  'Quarterly Report 2': 'Quarter #2 Audit',
  'Quarterly Report 3': 'Quarter #3 Audit',
  'Quarterly Report 4': 'Quarter #4 Audit',
};

export interface CreateCSAuditParams {
  accountId: string;
  accountName: string;
  templateName: string;
  phase1DueDate: string;
  phase2DueDate: string | null;
  phase2Required: boolean;
  /** Overall due date - equals phase1DueDate if no Phase 2, else phase2DueDate */
  dueDate: string;
}

/**
 * Flow 1: Determine if audit phase status should change based on PAW findings
 *
 * Rules:
 * 1. Initial Deletion template + All Phase 1 PAWs have findings → Audit Complete
 * 2. NOT Initial Deletion + Any PAW has "Listing/s found" → Mark that PAW as Phase 2 Required
 * 3. Phase 2 + All Phase 2 Required PAWs have findings → Audit Complete
 */
export const evaluatePhaseStatus = (
  audit: CSAuditRecord,
  pawRecords: PAWRecord[]
): {
  newPhaseStatus: string | null;
  pawUpdates: { id: string; Phase_2_Required__c: boolean }[];
} => {
  const hasCompletedFinding = (finding: string | null) => {
    const value = finding?.trim() ?? '';
    return value !== '' && value !== '-- Select --';
  };

  // === DEBUG LOGGING ===
  console.log('=== EVALUATE PHASE STATUS DEBUG ===');
  console.log('Audit ID:', audit.id);
  console.log('Audit_Template__c:', audit.Audit_Template__c);
  console.log('Current Phase_Status__c:', audit.Phase_Status__c);
  console.log('PAW Records count:', pawRecords.length);

  const isInitialDeletion = audit.Audit_Template__c === 'Initial Deletion';

  console.log('Is Initial Deletion:', isInitialDeletion);

  // Separate PAWs by phase
  const phase1PAWs = pawRecords.filter((p) => p.Audit_Phase__c === 'Phase 1');
  const phase2PAWs = pawRecords.filter((p) => p.Audit_Phase__c === 'Phase 2');

  console.log('Phase 1 PAWs count:', phase1PAWs.length);
  console.log('Phase 2 PAWs count:', phase2PAWs.length);

  // Log each PAW's details
  pawRecords.forEach((p, i) => {
    console.log(`PAW ${i}: id="${p.id}", Audit_Phase__c="${p.Audit_Phase__c}", Findings__c="${p.Findings__c}", Phase_2_Required__c=${p.Phase_2_Required__c}`);
  });

  // Track PAWs that need Phase 2 Required update
  const pawUpdates: { id: string; Phase_2_Required__c: boolean }[] = [];

  // Check Phase 1 completion
  const allPhase1Complete =
    phase1PAWs.length > 0 && phase1PAWs.every((p) => hasCompletedFinding(p.Findings__c));
  console.log('All Phase 1 Complete:', allPhase1Complete);
  console.log('Phase 1 PAWs findings check:', phase1PAWs.map(p => ({ id: p.id, findings: p.Findings__c, hasFindings: hasCompletedFinding(p.Findings__c) })));

  // Rule 1: Initial Deletion - if all Phase 1 complete, audit is done
  console.log('Checking Rule 1: isInitialDeletion=', isInitialDeletion, 'allPhase1Complete=', allPhase1Complete);
  if (isInitialDeletion && allPhase1Complete) {
    console.log('>>> RULE 1 TRIGGERED: Returning Audit Complete');
    return { newPhaseStatus: 'Audit Complete', pawUpdates: [] };
  }

  // Rule 2: NOT Initial Deletion - mark PAWs with listings as Phase 2 Required
  if (!isInitialDeletion) {
    for (const paw of phase1PAWs) {
      const hasListing =
        paw.Findings__c === 'Listing/s found' ||
        paw.Findings__c === 'Listing/s found and deletion has been requested';

      // Only add update if state is changing
      if (hasListing && !paw.Phase_2_Required__c) {
        pawUpdates.push({ id: paw.id, Phase_2_Required__c: true });
      } else if (!hasListing && paw.Phase_2_Required__c) {
        pawUpdates.push({ id: paw.id, Phase_2_Required__c: false });
      }
    }
  }

  // Rule 3: Check if Phase 2 is complete
  // Get all PAWs that require Phase 2
  const phase2RequiredPAWs = phase2PAWs.filter((p) => p.Phase_2_Required__c === true);

  if (phase2RequiredPAWs.length > 0) {
    const allPhase2Complete = phase2RequiredPAWs.every(
      (p) => hasCompletedFinding(p.Findings__c)
    );

    if (allPhase2Complete && allPhase1Complete) {
      return { newPhaseStatus: 'Audit Complete', pawUpdates };
    }
  }

  // If Phase 1 is complete but there are Phase 2 Required items pending
  if (allPhase1Complete && !isInitialDeletion) {
    const anyPhase2Required = phase1PAWs.some(
      (p) =>
        p.Findings__c === 'Listing/s found' ||
        p.Findings__c === 'Listing/s found and deletion has been requested'
    );

    if (anyPhase2Required) {
      // Check if we're already in Phase 2
      if (audit.Phase_Status__c !== 'Phase 2 - In Progress') {
        return { newPhaseStatus: 'Phase 2 - In Progress', pawUpdates };
      }
    } else {
      // No listings found anywhere, audit complete
      return { newPhaseStatus: 'Audit Complete', pawUpdates };
    }
  }

  // Rule 0: If status is "Not Started" and ANY PAW has findings, move to "In Progress"
  const isPhase1NotStarted = audit.Phase_Status__c === 'Phase 1 - Not Started' || audit.Phase_Status__c === 'Not Started';
  const hasAnyFindings = pawRecords.some((p) => hasCompletedFinding(p.Findings__c));

  console.log('Checking Rule 0: isPhase1NotStarted=', isPhase1NotStarted, 'hasAnyFindings=', hasAnyFindings);

  if (isPhase1NotStarted && hasAnyFindings) {
    console.log('>>> RULE 0 TRIGGERED: Not Started → In Progress');
    return { newPhaseStatus: 'Phase 1 - In Progress', pawUpdates };
  }

  // No status change needed
  console.log('>>> NO STATUS CHANGE: Returning null with', pawUpdates.length, 'PAW updates');
  console.log('=== END EVALUATE PHASE STATUS DEBUG ===');
  return { newPhaseStatus: null, pawUpdates };
};

/**
 * Get the new Account Status based on completed audit template
 */
export const getNextAccountStatus = (auditTemplateName: string): string | null => {
  return TEMPLATE_TO_ACCOUNT_STATUS[auditTemplateName] || null;
};

/**
 * Flow 2: Update Account Status when audit completes
 * Call this when Phase_Status__c changes to "Audit Complete"
 */
export const onAuditComplete = async (
  audit: CSAuditRecord,
  getAccount: (accountId: string) => Promise<AccountRecord>,
  updateAccount: (id: string, data: Partial<AccountRecord>) => Promise<void>
): Promise<{ newAccountStatus: string | null; accountId: string }> => {
  // Get the account ID from the audit (stored as Account__c or accountId)
  const accountId = audit.Account__c || audit.accountId;

  if (!accountId) {
    console.warn('No account ID found on audit record');
    return { newAccountStatus: null, accountId: '' };
  }

  const newStatus = getNextAccountStatus(audit.Audit_Template__c);

  if (!newStatus) {
    console.warn(`No account status mapping for template: ${audit.Audit_Template__c}`);
    return { newAccountStatus: null, accountId };
  }

  // Update the account
  await updateAccount(accountId, {
    CS_Account_Status__c: newStatus,
  });

  console.log(`Flow 2: Updated Account ${accountId} status to "${newStatus}"`);

  // Return info for Flow 3
  return { newAccountStatus: newStatus, accountId };
};

/**
 * Get the template to use for the next audit based on new Account Status
 * Fetches from Gobodo/RDS, with fallback to static templates
 */
export const getNextAuditTemplate = async (newAccountStatus: string): Promise<GobodoAuditTemplate | null> => {
  const templateName = ACCOUNT_STATUS_TO_TEMPLATE[newAccountStatus];
  console.log('[getNextAuditTemplate] Looking up template for status:', newAccountStatus);
  console.log('[getNextAuditTemplate] Template name from mapping:', templateName);

  if (!templateName) {
    console.log('[getNextAuditTemplate] No template mapping found for status');
    return null;
  }

  try {
    // Try to fetch from Gobodo first
    const gobodoTemplate = await fetchAuditTemplateByName(templateName);
    console.log('[getNextAuditTemplate] Gobodo template result:', gobodoTemplate);

    if (gobodoTemplate) {
      return gobodoTemplate;
    }

    // Fallback to static templates if Gobodo returns nothing
    console.log('[getNextAuditTemplate] Gobodo returned null, trying static fallback');
    const staticTemplate = AUDIT_TEMPLATES.find((t) => t.name === templateName);

    if (staticTemplate) {
      console.log('[getNextAuditTemplate] Using static template:', staticTemplate.name);
      // Convert static template to GobodoAuditTemplate format
      return {
        id: staticTemplate.id,
        name: staticTemplate.name,
        phase1DueDateDays: staticTemplate.phase1DueDateDays,
        phase2DueDateDays: staticTemplate.phase2DueDateDays,
        phase2Required: staticTemplate.phase2Required,
        checklistType: staticTemplate.checklistType || '',
        ownerId: '',
        ownerName: '',
        createdById: '',
        createdByName: '',
        createdDate: '',
        lastModifiedById: '',
        lastModifiedByName: '',
        lastModifiedDate: '',
      };
    }

    console.log('[getNextAuditTemplate] No template found in static data either');
    return null;
  } catch (error) {
    console.error('[getNextAuditTemplate] Error fetching template:', error);

    // Fallback to static templates on error
    const staticTemplate = AUDIT_TEMPLATES.find((t) => t.name === templateName);
    if (staticTemplate) {
      console.log('[getNextAuditTemplate] Using static fallback after error:', staticTemplate.name);
      return {
        id: staticTemplate.id,
        name: staticTemplate.name,
        phase1DueDateDays: staticTemplate.phase1DueDateDays,
        phase2DueDateDays: staticTemplate.phase2DueDateDays,
        phase2Required: staticTemplate.phase2Required,
        checklistType: staticTemplate.checklistType || '',
        ownerId: '',
        ownerName: '',
        createdById: '',
        createdByName: '',
        createdDate: '',
        lastModifiedById: '',
        lastModifiedByName: '',
        lastModifiedDate: '',
      };
    }
    return null;
  }
};

/**
 * Calculate due dates based on template from Gobodo/RDS
 */
export const calculateAuditDueDates = (
  template: GobodoAuditTemplate
): {
  phase1DueDate: string;
  phase2DueDate: string | null;
} => {
  const now = new Date();

  const phase1DueDate = new Date(now);
  phase1DueDate.setDate(phase1DueDate.getDate() + template.phase1DueDateDays);

  let phase2DueDate: string | null = null;
  // phase2Required is derived from phase2DueDateDays > 0 in the GobodoAuditTemplate mapper
  if (template.phase2Required && template.phase2DueDateDays > 0) {
    const p2Date = new Date(phase1DueDate);
    p2Date.setDate(p2Date.getDate() + template.phase2DueDateDays);
    phase2DueDate = p2Date.toISOString();
  }

  return {
    phase1DueDate: phase1DueDate.toISOString(),
    phase2DueDate,
  };
};

/**
 * Flow 3: Create next CS Audit when Account Status changes
 * Call this after Account Status is updated (from Flow 2)
 */
export const onAccountStatusChange = async (
  accountId: string,
  accountName: string,
  newAccountStatus: string,
  createCSAudit: (params: CreateCSAuditParams) => Promise<{ id: string }>
): Promise<{ auditCreated: boolean; newAuditId: string | null; templateName: string | null }> => {
  console.log('=== FLOW 3: onAccountStatusChange ===');
  console.log('accountId:', accountId);
  console.log('accountName:', accountName);
  console.log('newAccountStatus:', newAccountStatus);
  console.log('ACCOUNT_STATUS_TO_TEMPLATE mapping:', ACCOUNT_STATUS_TO_TEMPLATE);
  console.log('Template name for this status:', ACCOUNT_STATUS_TO_TEMPLATE[newAccountStatus]);

  // Fetch template from Gobodo/RDS
  const template = await getNextAuditTemplate(newAccountStatus);

  console.log('getNextAuditTemplate result:', template);

  if (!template) {
    console.error('>>> FLOW 3 FAILED: No template found for status:', newAccountStatus);
    return { auditCreated: false, newAuditId: null, templateName: null };
  }

  const { phase1DueDate, phase2DueDate } = calculateAuditDueDates(template);

  // Overall due date: if Phase 2 is required, use phase2DueDate; otherwise use phase1DueDate
  const dueDate = template.phase2Required && phase2DueDate ? phase2DueDate : phase1DueDate;

  console.log('Calculated due dates:', { phase1DueDate, phase2DueDate, dueDate });
  console.log('Template phase2Required:', template.phase2Required);

  const createParams: CreateCSAuditParams = {
    accountId,
    accountName,
    templateName: template.name,
    phase1DueDate,
    phase2DueDate,
    phase2Required: template.phase2Required,
    dueDate,
  };

  console.log('>>> FLOW 3: Calling createCSAudit with params:', createParams);

  try {
    const newAudit = await createCSAudit(createParams);

    console.log('>>> FLOW 3 SUCCESS: Created new audit');
    console.log(`Flow 3: Created new "${template.name}" audit (${newAudit.id}) for Account ${accountId}`);

    return {
      auditCreated: true,
      newAuditId: newAudit.id,
      templateName: template.name,
    };
  } catch (error) {
    console.error('>>> FLOW 3 ERROR: Failed to create next audit:', error);
    return { auditCreated: false, newAuditId: null, templateName: template.name };
  }
};

/**
 * Hook this into the PAW save function
 * Call this after any PAW Findings__c update
 * Chains Flow 1 → Flow 2
 */
export const onPAWFindingsChange = async (
  auditId: string,
  getAudit: (id: string) => Promise<CSAuditRecord>,
  getPAWsForAudit: (auditId: string) => Promise<PAWRecord[]>,
  updateAudit: (id: string, data: Partial<CSAuditRecord>) => Promise<void>,
  updatePAW: (id: string, data: Partial<PAWRecord>) => Promise<void>,
  // Flow 2 dependencies
  getAccount: (accountId: string) => Promise<AccountRecord>,
  updateAccount: (id: string, data: Partial<AccountRecord>) => Promise<void>
): Promise<{
  auditComplete: boolean;
  audit: CSAuditRecord;
  newAccountStatus: string | null;
  accountId: string;
}> => {
  const audit = await getAudit(auditId);
  const paws = await getPAWsForAudit(auditId);

  const { newPhaseStatus, pawUpdates } = evaluatePhaseStatus(audit, paws);

  // Update PAWs that need Phase_2_Required__c changes
  for (const update of pawUpdates) {
    await updatePAW(update.id, { Phase_2_Required__c: update.Phase_2_Required__c });
  }

  // Update audit phase status if changed
  if (newPhaseStatus && newPhaseStatus !== audit.Phase_Status__c) {
    await updateAudit(auditId, {
      Phase_Status__c: newPhaseStatus,
      // Add date stamps based on new status
      ...(newPhaseStatus === 'Phase 2 - In Progress' && {
        phase2StartDate: new Date().toISOString(),
      }),
      ...(newPhaseStatus === 'Audit Complete' && {
        phase2CompletedDate: new Date().toISOString(),
      }),
    });

    // If audit complete, trigger Flow 2
    if (newPhaseStatus === 'Audit Complete') {
      const updatedAudit = { ...audit, Phase_Status__c: newPhaseStatus };
      const { newAccountStatus, accountId } = await onAuditComplete(
        updatedAudit,
        getAccount,
        updateAccount
      );

      return {
        auditComplete: true,
        audit: updatedAudit,
        newAccountStatus,
        accountId,
      };
    }
  }

  return {
    auditComplete: false,
    audit,
    newAccountStatus: null,
    accountId: '',
  };
};

/**
 * Master function: Chain all flows together
 * Call this from the inline editing save handler when Findings change
 */
export const processAuditWorkflow = async (
  auditId: string,
  // Data fetchers
  getAudit: (id: string) => Promise<CSAuditRecord>,
  getPAWsForAudit: (auditId: string) => Promise<PAWRecord[]>,
  getAccount: (accountId: string) => Promise<AccountRecord>,
  // Data updaters
  updateAudit: (id: string, data: Partial<CSAuditRecord>) => Promise<void>,
  updatePAW: (id: string, data: Partial<PAWRecord>) => Promise<void>,
  updateAccount: (id: string, data: Partial<AccountRecord>) => Promise<void>,
  createCSAudit: (params: CreateCSAuditParams) => Promise<{ id: string }>,
  findExistingNextAudit?: (accountId: string, templateName: string) => Promise<{ id: string } | null>
): Promise<{
  flow1Result: { phaseStatusChanged: boolean; newPhaseStatus: string | null };
  flow2Result: { accountStatusChanged: boolean; newAccountStatus: string | null };
  flow3Result: { auditCreated: boolean; newAuditId: string | null; templateName: string | null };
}> => {
  console.log('=== PROCESS AUDIT WORKFLOW START ===');
  console.log('auditId:', auditId);

  // Flow 1: Evaluate PAW findings → Phase Status
  const audit = await getAudit(auditId);
  console.log('>>> Fetched audit:', JSON.stringify(audit, null, 2));
  console.log('audit.Audit_Template__c:', audit.Audit_Template__c);
  console.log('audit.Phase_Status__c:', audit.Phase_Status__c);
  console.log('audit.Account__c:', audit.Account__c);

  const paws = await getPAWsForAudit(auditId);
  console.log('>>> Fetched PAWs count:', paws.length);
  console.log('>>> Fetched PAWs:', JSON.stringify(paws, null, 2));

  if (paws.length > 0) {
    console.log('First PAW Audit_Phase__c:', paws[0].Audit_Phase__c);
    console.log('First PAW Findings__c:', paws[0].Findings__c);
  }

  // Check if template is "Initial Deletion" - this is critical for Flow 1
  const isInitialDeletion = audit.Audit_Template__c === 'Initial Deletion';
  console.log('Is Initial Deletion template?', isInitialDeletion);

  const { newPhaseStatus, pawUpdates } = evaluatePhaseStatus(audit, paws);
  console.log('>>> evaluatePhaseStatus result:', { newPhaseStatus, pawUpdatesCount: pawUpdates.length });

  // Apply PAW updates
  for (const update of pawUpdates) {
    await updatePAW(update.id, { Phase_2_Required__c: update.Phase_2_Required__c });
  }

  let flow1Result = { phaseStatusChanged: false, newPhaseStatus: null as string | null };
  let flow2Result = { accountStatusChanged: false, newAccountStatus: null as string | null };
  let flow3Result = {
    auditCreated: false,
    newAuditId: null as string | null,
    templateName: null as string | null,
  };

  // Update phase status if changed
  if (newPhaseStatus && newPhaseStatus !== audit.Phase_Status__c) {
    const now = new Date().toISOString();

    await updateAudit(auditId, {
      Phase_Status__c: newPhaseStatus,
      phaseStatus: newPhaseStatus,
      // Phase 1 - In Progress: Set start date/time
      ...(newPhaseStatus === 'Phase 1 - In Progress' && {
        Phase_1_Start_Date_Time__c: now,
        phase1StartDateTime: now,
      }),
      // Phase 2 - In Progress: Set phase 2 start date
      ...(newPhaseStatus === 'Phase 2 - In Progress' && {
        Phase_2_Start_Date_Time__c: now,
        phase2StartDateTime: now,
      }),
      // Audit Complete: Set completed dates based on Phase 2 Required
      ...(newPhaseStatus === 'Audit Complete' && {
        // Always set Phase 1 Completed Date/Time when audit completes
        Phase_1_Completed_Date_Time__c: audit.Phase_1_Completed_Date_Time__c ?? now,
        phase1CompletedDateTime: audit.phase1CompletedDateTime ?? audit.Phase_1_Completed_Date_Time__c ?? now,
        // Only set Phase 2 Completed Date/Time if Phase 2 was required
        ...(audit.Phase_2_Required__c && {
          Phase_2_Completed_Date_Time__c: now,
          phase2CompletedDateTime: now,
        }),
      }),
      // Phase 1 Completed: Set phase 1 completed date
      ...(newPhaseStatus === 'Phase 1 - Completed' && {
        Phase_1_Completed_Date_Time__c: now,
        phase1CompletedDateTime: now,
      }),
      // Always update Last Modified fields
      LastModifiedDate__c: now,
      lastModifiedDate: now,
    });

    flow1Result = { phaseStatusChanged: true, newPhaseStatus };
  }

  // Determine the effective phase status (either newly set or already existing)
  const effectivePhaseStatus = newPhaseStatus || audit.Phase_Status__c;
  console.log('=== CHECKING FLOW 2/3 ===');
  console.log('newPhaseStatus:', newPhaseStatus);
  console.log('audit.Phase_Status__c:', audit.Phase_Status__c);
  console.log('effectivePhaseStatus:', effectivePhaseStatus);

  // Flow 2: If audit IS complete (either just changed or already was), update Account Status
  if (effectivePhaseStatus === 'Audit Complete') {
    console.log('=== FLOW 2: Audit Is Complete - Checking Account Status ===');

    // Try multiple field names for account ID
    const accountId = audit.Account__c || audit.accountId || (audit as any).custom__account || (audit as any).Custom__account__c;
    console.log('Account ID from audit:', accountId);
    console.log('audit.Account__c:', audit.Account__c);
    console.log('audit.accountId:', audit.accountId);

    if (accountId) {
      const account = await getAccount(accountId);
      console.log('Fetched account:', account);

      const newAccountStatus = getNextAccountStatus(audit.Audit_Template__c);
      console.log('audit.Audit_Template__c:', audit.Audit_Template__c);
      console.log('getNextAccountStatus result:', newAccountStatus);
      console.log('Current account status:', account.CS_Account_Status__c);

      // Flow 2: Update account status if it changed
      if (newAccountStatus && newAccountStatus !== account.CS_Account_Status__c) {
        console.log('>>> FLOW 2: Updating account status to:', newAccountStatus);
        await updateAccount(accountId, { CS_Account_Status__c: newAccountStatus });
        flow2Result = { accountStatusChanged: true, newAccountStatus };
      } else {
        console.log('>>> FLOW 2: Account status already correct:', account.CS_Account_Status__c);
      }

      // Flow 3: Always check if next audit needs to be created (decoupled from Flow 2)
      // This ensures the next audit is created even if account status was already set
      const effectiveAccountStatus = newAccountStatus || account.CS_Account_Status__c;
      const accountName = account.Name || account.name || '';

      console.log('=== FLOW 3: Checking if next audit needs to be created ===');
      console.log('effectiveAccountStatus for template lookup:', effectiveAccountStatus);

      const nextTemplate = await getNextAuditTemplate(effectiveAccountStatus);
      console.log('accountName for Flow 3:', accountName);
      console.log('nextTemplate:', nextTemplate);

      if (!nextTemplate) {
        console.log('>>> FLOW 3: No next template for status:', effectiveAccountStatus);
      } else if (findExistingNextAudit) {
        console.log('>>> FLOW 3: Checking for existing audit with template:', nextTemplate.name);
        const existingAudit = await findExistingNextAudit(accountId, nextTemplate.name);
        console.log('>>> FLOW 3: findExistingNextAudit result:', existingAudit);

        if (existingAudit) {
          console.log('>>> FLOW 3 SKIPPED: Existing next audit already found:', existingAudit.id);
          flow3Result = {
            auditCreated: false,
            newAuditId: existingAudit.id,
            templateName: nextTemplate.name,
          };
        } else {
          console.log('>>> FLOW 3: Creating new audit with template:', nextTemplate.name);
          flow3Result = await onAccountStatusChange(
            accountId,
            accountName,
            effectiveAccountStatus,
            createCSAudit
          );
          console.log('Flow 3 result:', flow3Result);
        }
      } else if (nextTemplate) {
        // No findExistingNextAudit function provided, create anyway
        console.log('>>> FLOW 3: Creating new audit with template:', nextTemplate.name);
        flow3Result = await onAccountStatusChange(
          accountId,
          accountName,
          effectiveAccountStatus,
          createCSAudit
        );
        console.log('Flow 3 result:', flow3Result);
      }
    } else {
      console.log('>>> FLOW 2 SKIPPED: No account ID found on audit');
    }
  } else {
    console.log('>>> FLOW 2/3 SKIPPED: effectivePhaseStatus is not "Audit Complete"');
  }

  return { flow1Result, flow2Result, flow3Result };
};
